/**
 * ChapterNav: the persistent navigation. It is deliberately tiny: a
 * wordmark, the current chapter, and a Menu pill. The menu opens the same
 * chapter list the entry page uses, set in the same large type, so
 * navigating always feels like moving between chapters of one story.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CHAPTERS, CONTACT_EMAIL, chapterForPath } from "../data/chapters";
import { TLink } from "./TransitionProvider";
import { useV5 } from "../lib/context";

export default function ChapterNav() {
  const { pathname } = useLocation();
  const { go } = useV5();
  const [open, setOpen] = useState(false);
  const current = chapterForPath(pathname);

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.documentElement.style.overflow = open ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.documentElement.style.overflow = ""; };
  }, [open]);

  const choose = (c) => {
    if (c.external) { window.open(c.href, "_blank", "noopener"); return; }
    setOpen(false);
    go(c.to, c.label);
  };

  return (
    <>
      <header className={`nav${open ? " is-open" : ""}`}>
        <TLink to="/" label="Digital Billions Group" className="nav-mark">Digital Billions Group</TLink>
        {current && <span className="nav-chapter t-cap">{current.num} / {String(CHAPTERS.length).padStart(2, "0")} · {current.short}</span>}
        <button type="button" className="nav-pill" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="v5-menu">
          <span className="btn-roll"><span className="btn-roll-a">{open ? "Close" : "Menu"}</span><span className="btn-roll-b" aria-hidden="true">{open ? "Close" : "Menu"}</span></span>
        </button>
      </header>

      <div id="v5-menu" className={`menu${open ? " is-open" : ""}`} aria-hidden={!open}>
        <nav className="menu-list" aria-label="Chapters">
          {CHAPTERS.map((c, i) => (
            <a
              key={c.id}
              href={c.external ? c.href : c.to}
              className={`menu-item${current && current.id === c.id ? " is-current" : ""}`}
              style={{ "--i": i }}
              tabIndex={open ? 0 : -1}
              {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              onClick={(e) => { if (!c.external) { e.preventDefault(); choose(c); } }}>
              <span className="menu-num t-cap">{c.num}</span>
              <span className="menu-mask"><span className="menu-label">{c.label}</span></span>
              <span className="menu-arrow" aria-hidden="true">{c.external ? "↗" : "→"}</span>
            </a>
          ))}
        </nav>
        <div className="menu-foot t-cap">
          <a href={`mailto:${CONTACT_EMAIL}`} tabIndex={open ? 0 : -1}>{CONTACT_EMAIL}</a>
          <span><TLink to="/login" label="Member login">Member login</TLink> · <a href="/terms">Terms</a> · <a href="/privacy">Privacy</a></span>
        </div>
      </div>
    </>
  );
}
